import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const COUNTRY_DEFAULTS = {
  ES: 10, PT: 23, FR: 20, DE: 19, IT: 22, GB: 20,
  MX: 16, AE: 5,  SA: 15, AR: 21, CL: 19, CO: 19, MA: 20,
};

const PRESET_RATES = [0, 5, 7, 10, 13, 16, 19, 20, 21, 22, 23];

export function FiscalTab({ restaurant, authAxios, onSaved }) {
  const fc = restaurant?.fiscal_config || {};
  const country = (restaurant?.country || "ES").toUpperCase();
  const countryDefault = COUNTRY_DEFAULTS[country] ?? 10;

  const currentRate = fc.default_vat_rate != null
    ? Number(fc.default_vat_rate)
    : (restaurant?.tax_rate != null ? Number(restaurant.tax_rate) : countryDefault);

  const isPreset = PRESET_RATES.includes(currentRate);
  const [rateMode, setRateMode] = useState(isPreset ? String(currentRate) : "custom");
  const [customRate, setCustomRate] = useState(isPreset ? "" : String(currentRate));
  const [taxIncluded, setTaxIncluded] = useState(fc.tax_included !== false);
  const [showBreakdown, setShowBreakdown] = useState(fc.show_breakdown !== false);
  const [saving, setSaving] = useState(false);

  const effectiveRate = rateMode === "custom" ? Number(customRate) : Number(rateMode);

  const handleSave = async () => {
    const rate = effectiveRate;
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error("Tasa de IVA no valida (0–100)");
      return;
    }
    setSaving(true);
    try {
      await authAxios.patch("/restaurant", {
        fiscal_config: {
          default_vat_rate: rate,
          tax_included: taxIncluded,
          show_breakdown: showBreakdown,
        },
      });
      toast.success("Configuracion fiscal guardada");
      if (onSaved) onSaved();
    } catch (err) {
      toast.error("Error guardando configuracion fiscal");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="uppercase">Configuracion Fiscal / IVA</CardTitle>
          <CardDescription>
            Pais actual: <strong>{country}</strong> — tipo general por defecto:{" "}
            <strong>{countryDefault}%</strong>. Configura aqui la tasa aplicada a los recibos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* VAT rate selector */}
          <div className="space-y-2">
            <Label>Tasa de IVA</Label>
            <Select value={rateMode} onValueChange={setRateMode}>
              <SelectTrigger className="bg-secondary border-transparent w-64">
                <SelectValue placeholder="Seleccionar tasa" />
              </SelectTrigger>
              <SelectContent>
                {PRESET_RATES.map(r => (
                  <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                ))}
                <SelectItem value="custom">Personalizado</SelectItem>
              </SelectContent>
            </Select>
            {rateMode === "custom" && (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={customRate}
                  onChange={e => setCustomRate(e.target.value)}
                  className="bg-secondary border-transparent w-32"
                  placeholder="Ej: 12.5"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Tasa efectiva aplicada: <strong>{isNaN(effectiveRate) ? "—" : `${effectiveRate}%`}</strong>
            </p>
          </div>

          {/* Tax included switch */}
          <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
            <div>
              <Label className="text-sm font-medium">Precios incluyen IVA</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Los precios del menu ya incluyen el impuesto (IVA incluido)
              </p>
            </div>
            <Switch checked={taxIncluded} onCheckedChange={setTaxIncluded} />
          </div>

          {/* Show breakdown switch */}
          <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
            <div>
              <Label className="text-sm font-medium">Mostrar desglose de IVA en el recibo</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Muestra base imponible e IVA separados en el ticket impreso
              </p>
            </div>
            <Switch checked={showBreakdown} onCheckedChange={setShowBreakdown} />
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : "Guardar configuracion fiscal"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
